import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  InputAdornment,
  IconButton,
  Autocomplete,
  CircularProgress,
  Switch,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { BsImages } from "react-icons/bs";
import { RiImageAddLine } from "react-icons/ri";
import "react-datepicker/dist/react-datepicker.css";
import { useQuery, useMutation } from "@tanstack/react-query";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment/moment";
import { useFormik } from "formik";

import {
  getDetailInventory,
  getGroups,
  updateProduct,
  getPlatform,
  getCategoryMaster,
  getPrincipal,
  getPackaging,
} from "../../../../services/InventoryAPI";

import useDebounce from "../../../hooks/useDebounce";
import ErrorPopup from "../../../common/particles.jsx/ErrorPopup";
import { IGNORE_TYPE } from "../../../constants/constant";
import LoadingBackdrop from "../../../common/particles.jsx/LoadingBackdrop";

const FETCH_LIMIT = 25;

const today = moment().format("YYYY-MM-DD");
const toDateApi = (date) => moment(date).format("YYYY-MM-DD");

const EditDetailProductHold = ({ isWarehouse }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(1);
  const [count, setCount] = useState(0);
  const [qtyType, setQtyType] = useState("");
  const [quantity, setQuantity] = useState(0);

  const [principalValue, setPrincipalValue] = useState([]);
  const [openPrincipal, setOpenPrincipal] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [optionPrincipal, setOptionPrincipal] = useState([]);
  const [optionCategory, setOptionCategory] = useState([]);
  const [searchPrincipal, setSearchPrincipal] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const debouncePrincipal = useDebounce(searchPrincipal, 500);

  const debounceCategory = useDebounce(searchCategory, 500);

  const pharmacyNetPriceFinal = (
    discountPercent,
    pharmacyNetPriceReal,
    taxValue,
  ) => {
    if (!pharmacyNetPriceReal) return 0;
    let discount =
      pharmacyNetPriceReal * (discountPercent ? discountPercent / 100 : 0);
    let value =
      (pharmacyNetPriceReal - discount) * (handleDpp(taxValue * 100) / 100);

    return pharmacyNetPriceReal - discount + value;
  };

  const handleDpp = (ppnVal) => {
    return ppnVal ? (ppnVal === 11 ? 11 : (11 / 12) * 12) : 0;
  };

  const { data } = useQuery({
    queryKey: ["detail_product"],
    queryFn: () => getDetailInventory(id, isWarehouse ? isWarehouse : false),
    keepPreviousData: true,
  });

  const { data: platforms } = useQuery({
    queryKey: ["platform"],
    queryFn: () => getPlatform(),
    keepPreviousData: true,
  });

  const { data: categories } = useQuery({
    queryKey: ["category", data, debounceCategory],
    queryFn: () => getCategoryMaster(FETCH_LIMIT, offset, debounceCategory),
    enabled: !!data,
  });

  const { data: principals, isLoading: isPrincipalLoading } = useQuery({
    queryKey: ["principal", data, debouncePrincipal, openPrincipal],
    queryFn: () => getPrincipal(FETCH_LIMIT, offset, debouncePrincipal),
    enabled: !!data,
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: updateProduct,
    onSuccess: () =>
      navigate(
        `${
          isWarehouse ? "/central-warehouse" : ""
        }/inventory/produk-hold/detail-product/${id}`,
      ),
  });

  const formik = useFormik({
    initialValues: {
      warehouse: isWarehouse ? isWarehouse : false,
      product_id: parseInt(id, 10),
      nie_number: data && data !== undefined ? data.nie_number : "",
      principal_id: data && data !== undefined ? data.product_principal_id : "",
      category_id:
        data &&
        data !== undefined &&
        data?.product_category_id?.length > 0 &&
        data?.product_category_id[0] !== 0
          ? data.product_category_id.map((item, i) => ({
              id: item,
              name: data?.product_category_name[i],
            }))
          : [],
      label_id: data && data !== undefined ? data.label_product : "",
      product_name: data && data !== undefined ? data.product_name : "",
      product_image: data && data !== undefined ? data.media : [],
      product_description: {
        indication:
          data && data !== undefined ? data.description.indication : "",
        composition:
          data && data !== undefined ? data.description.composition : "",
        dosage: data && data !== undefined ? data.description.dosage : "",
        usage: data && data !== undefined ? data.description.usage : "",
        contraindication:
          data && data !== undefined ? data.description.contraindication : "",
        side_effect:
          data && data !== undefined ? data.description.side_effect : "",
        storage_description:
          data && data !== undefined
            ? data.description.storage_description
            : "",
        shelf: data && data.shelf !== undefined ? data.shelf : "",
        bentuk_sediaan:
          data && data.bentuk_sediaan !== undefined ? data.bentuk_sediaan : "",
      },
      require_prescription:
        data && data !== undefined ? data.description.require_prescription : "",
      expired_date: data && data !== undefined ? data.expired_date : "",
      packaging: {
        type_box:
          data && data !== undefined
            ? data.packing?.type_box.replace(/\d+/g, "").trim()
            : "",

        total_box: data && data !== undefined ? data.packing?.total_box : 1,
        type_strip:
          data && data !== undefined
            ? data.packing?.type_strip.replace(/\d+/g, "").trim()
            : "",
        total_strip: data && data !== undefined ? data.packing?.total_strip : 1,
        type_unit:
          data && data !== undefined
            ? data.packing?.type_unit
                .replace(/[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?\s*/g, "")
                .trim()
            : "",
        total_unit: data && data !== undefined ? data.packing?.total_unit : 1,
      },
      price: {
        platform_price:
          data && data !== undefined && data?.platform_price !== null
            ? data.platform_price.map((p) => ({
                platform_id: p.platform_id,
                price: p.price,
              }))
            : [],
        pharmacy_net_price: data && data !== undefined ? data.hna_real : 0,
        sell_price: data && data !== undefined ? data.price : 0,
        sell_price_sekunder:
          data && data !== undefined ? data.price_sekunder : 0,
        sell_price_tersier: data && data !== undefined ? data.price_tersier : 0,
        price_grosir:
          data && data !== undefined && data.price_grosir
            ? data.price_grosir
            : [],
      },
      batch_details:
        data && data !== undefined
          ? data.batch_details
              .filter((item) => item.from_purchase === true)
              .map((item) => ({ ...item, quantity: item.stock }))
          : [],
      barcode: data && data !== undefined ? data.barcode : "",
      product_inventory_id:
        data && data !== undefined ? data.product_inventory_id : "",
      status_reminder_stock:
        data && data !== undefined ? data.status_reminder_stock : true,
      reminder_stock: data && data !== undefined ? data.reminder_stock : true,
      reminder_uom: data && data !== undefined ? data.reminder_uom : true,
      weight: data ? data.weight : 0,
      dimensions: {
        length: data ? data.dimensions.length : 0,
        width: data ? data.dimensions.width : 0,
        height: data ? data.dimensions.height : 0,
      },
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      mutate({
        ...values,
        category_id: values.category_id.map((data) => data.id),
        batch_details: values.batch_details.map((data) => {
          if (qtyType === values.packaging.type_unit) {
            return {
              quantity: data.quantity,
              number_batch: data.number_batch,
              expired_date: data.expired_date,
              from_purchase: data.from_purchase ? data.from_purchase : false,
            };
          }
          if (qtyType === values.packaging.type_strip) {
            if (IGNORE_TYPE.includes(values.packaging.type_unit)) {
              return {
                quantity: data.quantity * 1,
                number_batch: data.number_batch,
                expired_date: data.expired_date,
                from_purchase: data.from_purchase ? data.from_purchase : false,
              };
            } else {
              return {
                quantity: data.quantity * values.packaging.total_unit,
                number_batch: data.number_batch,
                expired_date: data.expired_date,
                from_purchase: data.from_purchase ? data.from_purchase : false,
              };
            }
          }
          if (qtyType === values.packaging.type_box) {
            if (IGNORE_TYPE.includes(values.packaging.type_unit)) {
              return {
                quantity: data.quantity * values.packaging.total_strip * 1,
                number_batch: data.number_batch,
                expired_date: data.expired_date,
                from_purchase: data.from_purchase ? data.from_purchase : false,
              };
            } else {
              return {
                quantity:
                  data.quantity *
                  values.packaging.total_strip *
                  values.packaging.total_unit *
                  1,
                number_batch: data.number_batch,
                expired_date: data.expired_date,
                from_purchase: data.from_purchase ? data.from_purchase : false,
              };
            }
          }
          return {
            quantity: data.quantity,
            number_batch: data.number_batch,
            expired_date: data.expired_date,
            from_purchase: data.from_purchase ? data.from_purchase : false,
          };
        }),
      });
    },
  });

  const { data: groups } = useQuery({
    queryKey: ["groups"],
    queryFn: () => getGroups(),
  });

  const { data: packaging } = useQuery({
    queryKey: ["packaging", data],
    queryFn: () => getPackaging(),
    keepPreviousData: true,
  });

  const intersection = new Set(
    formik.values.price.platform_price.map(({ platform_id }) => platform_id),
  );
  const result = platforms?.filter(({ id }) => !intersection.has(id));

  const finalStock = useMemo(() => {
    if (qtyType === formik.values.packaging.type_unit) {
      return count;
    }
    if (qtyType === formik.values.packaging.type_strip) {
      if (IGNORE_TYPE.includes(formik.values.packaging.type_unit)) {
        return count;
      } else {
        return count * formik.values.packaging.total_unit;
      }
    }
    if (qtyType === formik.values.packaging.type_box) {
      if (IGNORE_TYPE.includes(formik.values.packaging.type_unit)) {
        return count * formik.values.packaging.total_strip;
      } else {
        return (
          count *
          formik.values.packaging.total_unit *
          formik.values.packaging.total_strip
        );
      }
    }
    return 0;
  }, [count, formik, qtyType]);

  const handleAddPlatform = useCallback(() => {
    if (platforms && platforms !== undefined)
      formik.setFieldValue("price.platform_price", [
        ...formik.values.price.platform_price,
        { platform_id: result[0]?.id, price: "" },
      ]);
    else formik.setFieldValue("platform_price", []);
  }, [formik.values.price.platform_price, result]);

  const handleUploadImage = useCallback(
    (e) => {
      try {
        const files = e.currentTarget.files;
        if (files === null) {
          setErrorMessage("Something went wrong when reading the image");
          throw new Error("Something went wrong when reading the image");
        }
        const file = files.item(0);
        if (file === null) {
          setErrorMessage("Something went wrong when reading the image");
          throw new Error("Something went wrong when reading the image");
        }

        const reader = new FileReader();

        reader.onload = (e) => {
          if (!e.target || (e.target && !e.target.result)) {
            setErrorMessage("Something went wrong when reading the image");
            throw new Error("Something went wrong when reading the image");
          }

          const image = new Image();
          image.onload = () => {
            formik.setFieldValue("product_image", [image.src, image.src]);
          };
          image.onerror = () => {
            setErrorMessage("Something went wrong when reading the image");
            throw new Error("Something went wrong when reading the image");
          };
          image.src = e.target.result;
        };
        reader.onerror = () => {
          setErrorMessage("Something went wrong when reading the file");
          throw new Error("Something went wrong when reading the file");
        };

        reader.readAsDataURL(file);
      } catch (e) {
        if (e instanceof Error) {
        }
      }
    },
    [formik.setFieldValue],
  );

  useEffect(() => {
    if (openCategory || openPrincipal) {
      setOptionCategory(categories !== undefined ? categories : []);
      setOptionPrincipal(principals !== undefined ? principals : []);
    }
  }, [categories, principals, openPrincipal, openCategory]);

  useEffect(() => {
    setPrincipalValue({
      id: data && data !== undefined ? data.product_principal_id : "",
      name: data && data !== undefined ? data.product_principal_name : "",
    });
  }, [data]);

  return (
    <div className="container mx-auto w-full mt-16 lg:mt-0">
      <LoadingBackdrop isLoading={isLoading} />
      {data && data !== undefined ? (
        <>
          <div className="flex text-blue-500 gap-2">
            <p
              className="hover:cursor-pointer"
              onClick={() =>
                navigate(
                  `${
                    isWarehouse ? "/central-warehouse" : ""
                  }/inventory/produk-hold`,
                )
              }
            >
              Produk Hold
            </p>
            <p>/</p>
            <p>Update Information</p>
          </div>
          <>
            <form onSubmit={formik.handleSubmit}>
              <div className="grid grid-col-1 shadow-md rounded-lg mt-5 p-3">
                <h1 className="font-medium text-[20px] lg:text-[25px]">
                  Detail Produk
                </h1>
                <TextField
                  id="outlined-basic"
                  label="Nomor Izin Edar"
                  value={formik.values.nie_number}
                  onChange={(e) =>
                    formik.setFieldValue("nie_number", e.target.value)
                  }
                  disabled
                  variant="outlined"
                  className="mt-3"
                />

                <div className="input-foto">
                  <div className="bg-blue-500 p-3 text-white font-medium flex mt-4 items-center gap-3 text-[20px] rounded-t-lg">
                    <span>
                      <BsImages />
                    </span>
                    Foto Produk
                  </div>
                  <div className="flex items-center">
                    {data && data.media !== undefined ? (
                      <label
                        htmlFor="drop"
                        className="cursor-pointer flex flex-row items-center justify-center w-full h-full border-2 rounded-b-lg"
                      >
                        <div className="flex justify-center p-4 w-[40vh] h-[40vh] ">
                          <img
                            src={formik.values.product_image[0]}
                            alt="foto produk"
                            className="rounded-lg shadow-md w-full h-full object-cover"
                          />
                        </div>
                        <input
                          id="drop"
                          type="file"
                          hidden
                          accept="accept='image/jpeg,image/png,image/jpg"
                          onChange={(e) => handleUploadImage(e)}
                        />
                      </label>
                    ) : (
                      <label
                        htmlFor="dropzone-file1"
                        className="cursor-pointer flex flex-col items-center justify-center w-full h-64 border-2 rounded-b-lg"
                      >
                        <div className="flex flex-col items-center justify-center">
                          <RiImageAddLine className="text-[90px] text-blue-500" />
                          <p className="font-medium text-l text-blue-400 mt-3">
                            Upload foto product
                          </p>
                          <p className="text-center">
                            <span className="">Ukuran foto maksimal 1 mb</span>
                          </p>
                        </div>
                        <input
                          id="dropzone-file1"
                          type="file"
                          hidden
                          accept="accept='image/jpeg,image/png,image/jpg"
                          onChange={(e) => handleUploadImage(e)}
                        />
                      </label>
                    )}
                  </div>
                </div>
                <TextField
                  id="outlined-basic"
                  label="Nama Obat"
                  name="product_name"
                  variant="outlined"
                  value={formik.values.product_name}
                  onChange={(e) =>
                    formik.setFieldValue("product_name", e.target.value)
                  }
                  className="mt-3"
                />
                <Autocomplete
                  fullWidth
                  multiple
                  filterSelectedOptions
                  sx={{ marginTop: 3 }}
                  open={openCategory}
                  onOpen={() => setOpenCategory(true)}
                  onClose={() => {
                    setOpenCategory(false);
                    setSearchCategory("");
                  }}
                  disablePortal
                  options={optionCategory}
                  value={formik.values.category_id}
                  onChange={(_, inputValue) => {
                    formik.setFieldValue(
                      "category_id",
                      inputValue !== null ? inputValue : [],
                    );
                    setOptionCategory([]);
                  }}
                  onInputChange={(_, onInputValue, reason) => {
                    if (reason === "input") {
                      setSearchCategory(onInputValue);
                      setOptionCategory([]);
                    }
                  }}
                  getOptionLabel={(option) => (option ? option.name : "-")}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tambah Kategori"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                <TextField
                  id="outlined-basic"
                  name="indication"
                  value={formik.values.product_description.indication}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.indication",
                      e.target.value,
                    )
                  }
                  label="Indikasi / Kegunaan"
                  variant="outlined"
                  className="mt-3"
                />
                <TextField
                  id="outlined-basic"
                  name="composition"
                  value={formik.values.product_description.composition}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.composition",
                      e.target.value,
                    )
                  }
                  label="Kandungan / Komposisi"
                  variant="outlined"
                  className="mt-3"
                />
                <TextField
                  id="outlined-multiline-static"
                  name="dosage"
                  value={formik.values.product_description.dosage}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.dosage",
                      e.target.value,
                    )
                  }
                  label="Dosis"
                  multiline
                  rows={3}
                  className="mt-3"
                />
                <TextField
                  id="outlined-multiline-static"
                  name="usage"
                  label="Cara Pemakaian"
                  multiline
                  rows={2}
                  value={formik.values.product_description.usage}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.usage",
                      e.target.value,
                    )
                  }
                  className="mt-3"
                />
                <TextField
                  id="outlined-basic"
                  name="contraindication"
                  label="Kontradiksi / Jangan digunakan oleh"
                  value={formik.values.product_description.contraindication}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.contraindication",
                      e.target.value,
                    )
                  }
                  variant="outlined"
                  className="mt-3"
                />
                <TextField
                  id="outlined-basic"
                  label="Efek Samping"
                  name="side_effect"
                  multiline
                  rows={3}
                  variant="outlined"
                  className="mt-3"
                  value={formik.values.product_description.side_effect}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.side_effect",
                      e.target.value,
                    )
                  }
                />
                <TextField
                  id="outlined-basic"
                  label="Cara Penyimpanan"
                  name="storage_description"
                  variant="outlined"
                  className="mt-3"
                  value={formik.values.product_description.storage_description}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "product_description.storage_description",
                      e.target.value,
                    )
                  }
                />

                <FormControl fullWidth className="mt-3">
                  <InputLabel id="demo-simple-select-label">
                    Golongan Obat
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    name="label_id"
                    value={formik.values.label_id}
                    label="Golongan Obat"
                    onChange={(e) =>
                      formik.setFieldValue("label_id", e.target.value)
                    }
                  >
                    <MenuItem
                      sx={{ display: "none" }}
                      disabled
                      value=""
                    ></MenuItem>
                    {groups &&
                      groups.map((item) => {
                        return (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
                <TextField
                  id="outlined-basic"
                  label="require-prescription"
                  variant="filled"
                  className="mt-3"
                  disabled
                  value={formik.values.require_prescription ? "Ya" : "Tidak"}
                  onChange={(e) =>
                    formik.setFieldValue("require_prescription", e.target.value)
                  }
                />
                <Autocomplete
                  fullWidth
                  sx={{ marginTop: 3 }}
                  loading={isPrincipalLoading}
                  open={openPrincipal}
                  onOpen={() => setOpenPrincipal(true)}
                  onClose={() => setOpenPrincipal(false)}
                  disablePortal
                  options={optionPrincipal}
                  value={principalValue}
                  onChange={(_, inputValue) => {
                    setPrincipalValue(inputValue);
                    formik.setFieldValue("principal_id", inputValue?.id);
                    setOptionPrincipal([]);
                  }}
                  onInputChange={(_, onInputValue, reason) => {
                    if (reason === "input") {
                      setSearchPrincipal(onInputValue);
                      setOptionPrincipal([]);
                    }
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    );
                  }}
                  id="combo-box-demo"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Principal"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isPrincipalLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                <div className="flex flex-row gap-5">
                  <div className="w-full flex flex-col lg:flex-row mt-4 gap-4">
                    <div className="w-full">
                      <div className="grid grid-cols-2 gap-4 w-full">
                        <h1 className="mb-2 font-bold text-[10px] lg:text-[20px] col-span-2">
                          Kemasan
                        </h1>
                        <TextField
                          id="outlined-basic"
                          label="Qty"
                          variant="outlined"
                          disabled
                          value={formik.values.packaging.total_box}
                        />
                        <FormControl fullWidth>
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={formik.values.packaging.type_box}
                            disabled
                          >
                            <MenuItem value={data.packing.type_box}>
                              {data.packing.type_box}
                            </MenuItem>
                            <MenuItem value={data.packing.type_strip}>
                              Strip
                            </MenuItem>
                            <MenuItem value={data.packing.type_unit}>
                              Tablet
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          id="outlined-basic"
                          label="Qty"
                          variant="outlined"
                          disabled
                          value={formik.values.packaging.total_strip}
                        />
                        <FormControl fullWidth>
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            disabled
                            value={formik.values.packaging.type_strip}
                          >
                            <MenuItem value={data.packing.type_box}>
                              {data.packing.type_box}
                            </MenuItem>
                            <MenuItem value={data.packing.type_strip}>
                              {data.packing.type_strip}
                            </MenuItem>
                            <MenuItem value={data.packing.type_unit}>
                              {data.packing.type_unit}
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          id="outlined-basic"
                          label="Qty"
                          variant="outlined"
                          disabled
                          value={formik.values.packaging.total_unit}
                        />
                        <FormControl fullWidth>
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            disabled
                            value={formik.values.packaging.type_unit}
                          >
                            <MenuItem value={data.packing.type_box}>
                              {data.packing.type_box}
                            </MenuItem>
                            <MenuItem value={data.packing.type_strip}>
                              {data.packing.type_strip}
                            </MenuItem>
                            <MenuItem value={data.packing.type_unit}>
                              {data.packing.type_unit}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    <div className="w-full">
                      <h1 className="mb-4 font-bold text-[10px] lg:text-[20px] col-span-2">
                        Berat
                      </h1>
                      <TextField
                        fullWidth
                        label="Berat"
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">
                              GRAM
                            </InputAdornment>
                          ),
                        }}
                        value={formik.values.weight}
                        onChange={(e) => {
                          let numericValue = e.target.value;
                          if (numericValue === "") {
                            numericValue = "0";
                          }
                          formik.setFieldValue(
                            "weight",
                            parseInt(numericValue.replace(/[^0-9]/g, ""), 10),
                          );
                        }}
                        sx={{ marginBottom: 2 }}
                      />
                      <h1 className="mb-4 font-bold text-[10px] lg:text-[20px] col-span-2">
                        Dimensi
                      </h1>
                      <div className="w-full flex gap-4">
                        <TextField
                          fullWidth
                          label="Panjang"
                          variant="outlined"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">
                                CM
                              </InputAdornment>
                            ),
                          }}
                          value={formik.values.dimensions.length}
                          onChange={(e) => {
                            let numericValue = e.target.value;
                            if (numericValue === "") {
                              numericValue = "0";
                            }
                            formik.setFieldValue(
                              "dimensions.length",
                              parseInt(numericValue.replace(/[^0-9]/g, ""), 10),
                            );
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Lebar"
                          variant="outlined"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">
                                CM
                              </InputAdornment>
                            ),
                          }}
                          value={formik.values.dimensions.width}
                          onChange={(e) => {
                            let numericValue = e.target.value;
                            if (numericValue === "") {
                              numericValue = "0";
                            }
                            formik.setFieldValue(
                              "dimensions.width",
                              parseInt(numericValue.replace(/[^0-9]/g, ""), 10),
                            );
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Tinggi"
                          variant="outlined"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">
                                CM
                              </InputAdornment>
                            ),
                          }}
                          value={formik.values.dimensions.height}
                          onChange={(e) => {
                            let numericValue = e.target.value;
                            if (numericValue === "") {
                              numericValue = "0";
                            }
                            formik.setFieldValue(
                              "dimensions.height",
                              parseInt(numericValue.replace(/[^0-9]/g, ""), 10),
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {data.batch_details.filter(
                  (item) => item.from_purchase === true,
                ).length > 0 ? (
                  <div>
                    {formik.values.batch_details.map((b, index) => (
                      <div
                        className="flex justify-evenly mt-4 gap-2"
                        key={index}
                      >
                        <TextField
                          sx={{ width: "100%" }}
                          id="outlined-basic"
                          label="Nomor Batch"
                          variant="outlined"
                          value={b.number_batch}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "batch_details",
                              formik.values.batch_details.map((b1, idx) => {
                                return index === idx
                                  ? { ...b, number_batch: e.target.value }
                                  : b1;
                              }),
                            )
                          }
                        />
                        <TextField
                          sx={{ width: "100%" }}
                          id="outlined-basic"
                          label="Kuantitas"
                          variant="outlined"
                          disabled={!data}
                          value={b.quantity}
                          onChange={(e) => {
                            let numericValue = e.target.value;
                            if (numericValue === "") {
                              numericValue = "0";
                            }
                            formik.setFieldValue(
                              "batch_details",
                              formik.values.batch_details.map((b1, idx) => {
                                return index === idx
                                  ? {
                                      ...b,
                                      quantity: parseInt(
                                        numericValue.replace(/[^0-9]/g, ""),
                                        10,
                                      ),
                                    }
                                  : b1;
                              }),
                            );
                          }}
                        />
                        <>
                          <DesktopDatePicker
                            views={["year", "month", "day"]}
                            label="Tanggal Expired"
                            sx={{ width: "100%" }}
                            value={
                              b.expired_date !== "" ? b.expired_date : null
                            }
                            onChange={(newExpiredDate) =>
                              formik.setFieldValue(
                                "batch_details",
                                formik.values.batch_details.map((b1, idx) => {
                                  return index === idx
                                    ? {
                                        ...b,
                                        expired_date: toDateApi(newExpiredDate),
                                      }
                                    : b1;
                                }),
                              )
                            }
                            renderInput={(params) => {
                              return <TextField {...params} fullWidth />;
                            }}
                            disablePast={true}
                          />
                        </>
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              if (formik.values.batch_details.length === 1) {
                                formik.setFieldValue("batch_details", []);
                              } else {
                                formik.setFieldValue("batch_details", [
                                  ...formik.values.batch_details.slice(
                                    0,
                                    index,
                                  ),
                                  ...formik.values.batch_details.slice(
                                    index + 1,
                                  ),
                                ]);
                              }
                            }}
                          >
                            <DeleteIcon sx={{ color: "black" }} />
                          </button>
                        </>
                      </div>
                    ))}
                    <p
                      className="text-blue-500 hover:underline hover:text-blue-500 cursor-pointer mt-4 text-end"
                      onClick={() => {
                        formik.setFieldValue("batch_details", [
                          ...formik.values.batch_details,
                          {
                            quantity: 0,
                            number_batch: "",
                            expired_date: "",
                            tanggal_input: today,
                            from_purchase:
                              data.batch_details.filter(
                                (item) => item.from_purchase === true,
                              ).length > 0,
                          },
                        ]);
                      }}
                    >
                      +Tambah Batch lain
                    </p>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 justify-between mt-4 items-center">
                  <p className="font-bold">HNA</p>
                  <TextField
                    id="outlined-basic"
                    label="HNA"
                    variant="outlined"
                    disabled
                    fullWidth
                    value={formik.values.price.pharmacy_net_price}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "price.pharmacy_net_price",
                        Number(e.target.value),
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp.</InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 justify-between mt-4 items-center">
                  <p className="font-bold">Diskon</p>
                  <TextField
                    id="outlined-basic"
                    label="Diskon"
                    variant="outlined"
                    fullWidth
                    value={data ? data.hna_discount : 0}
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 justify-between mt-4 items-center">
                  <p className="font-bold">Harga Akhir HNA</p>
                  <TextField
                    id="outlined-basic"
                    label="Harga Akhir HNA"
                    variant="outlined"
                    fullWidth
                    // value={pharmacyNetPriceFinal(
                    //   data ? data.hna_discount : 0,
                    //   formik.values.price.pharmacy_net_price,
                    //   data ? data.hna_ppn_value : 0
                    // )}
                    value={data ? data.hna : 0}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp.</InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 justify-between mt-4 items-center">
                  <p className="font-bold">DPP</p>
                  <TextField
                    id="outlined-basic"
                    label="DPP"
                    variant="outlined"
                    fullWidth
                    value={data ? data.dpp : 0}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp.</InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 justify-between mt-4 items-center">
                  <p className="font-bold">PPN</p>
                  <TextField
                    id="outlined-basic"
                    label="PPN"
                    variant="outlined"
                    fullWidth
                    value={
                      data
                        ? data.hna_ppn_value < 1
                          ? data.hna_ppn_value * 100
                          : data.hna_ppn_value
                        : 0
                    }
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </div>
                {isWarehouse ? null : (
                  <>
                    {" "}
                    <div className="grid grid-cols-2 justify-between mt-4 items-center">
                      <p className="font-bold">Harga Jual</p>
                      <TextField
                        id="outlined-basic"
                        label="Harga Jual"
                        variant="outlined"
                        fullWidth
                        value={formik.values.price.sell_price}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "price.sell_price",
                            Number(e.target.value),
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              Rp.
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 justify-between mt-4 items-center">
                      <p className="font-bold">Harga Jual Sekunder</p>
                      <TextField
                        id="outlined-basic"
                        label="Harga Jual Sekunder"
                        variant="outlined"
                        fullWidth
                        value={formik.values.price.sell_price_sekunder}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "price.sell_price_sekunder",
                            Number(e.target.value),
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              Rp.
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 justify-between mt-4 items-center">
                      <p className="font-bold">Harga Jual Tersier</p>
                      <TextField
                        id="outlined-basic"
                        label="Harga Jual Tersier"
                        variant="outlined"
                        fullWidth
                        value={formik.values.price.sell_price_tersier}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "price.sell_price_tersier",
                            Number(e.target.value),
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              Rp.
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  </>
                )}
                <TextField
                  fullWidth
                  label="Barcode"
                  sx={{ marginTop: 3 }}
                  value={formik.values.barcode}
                  onChange={(e) =>
                    formik.setFieldValue("barcode", e.target.value)
                  }
                />
                <TextField
                  id="outlined-basic"
                  label="Rak (optional)"
                  className="mt-3"
                  value={formik.values.shelf}
                  onChange={(e) =>
                    formik.setFieldValue("shelf", e.target.value)
                  }
                />
                <TextField
                  id="outlined-basic"
                  label="Bentuk Sediaan"
                  className="mt-3"
                  value={formik.values.bentuk_sediaan}
                  onChange={(e) =>
                    formik.setFieldValue("bentuk_sediaan", e.target.value)
                  }
                />

                {formik.values.price.price_grosir?.map((g, index) => {
                  return (
                    <div className="my-2 w-full" key={index}>
                      <div className="w-full flex gap-2 items-center">
                        {index > 0 ? (
                          <div className="w-full basis-1/3"></div>
                        ) : (
                          <p className="font-bold mt-2 w-full basis-1/3">
                            Harga Grosir
                          </p>
                        )}
                        <div className="w-full flex gap-2 items-center">
                          <TextField
                            fullWidth
                            value={g.minimum}
                            disabled={!data}
                            onChange={(e) => {
                              let numericValue = e.target.value;
                              if (numericValue === "") {
                                numericValue = "0";
                              }
                              formik.setFieldValue(
                                "price.price_grosir",
                                formik.values.price.price_grosir?.map(
                                  (g1, idx) => {
                                    return index === idx
                                      ? {
                                          ...g,
                                          minimum: parseInt(
                                            numericValue.replace(/[^0-9]/g, ""),
                                            10,
                                          ),
                                        }
                                      : g1;
                                  },
                                ),
                              );
                            }}
                          />
                          <p>S/D</p>
                          <TextField
                            fullWidth
                            value={g.maximum}
                            disabled={!data}
                            onChange={(e) => {
                              let numericValue = e.target.value;
                              if (numericValue === "") {
                                numericValue = "0";
                              }
                              formik.setFieldValue(
                                "price.price_grosir",
                                formik.values.price.price_grosir.map(
                                  (g1, idx) => {
                                    return index === idx
                                      ? {
                                          ...g,
                                          maximum: parseInt(
                                            numericValue.replace(/[^0-9]/g, ""),
                                            10,
                                          ),
                                        }
                                      : g1;
                                  },
                                ),
                              );
                            }}
                          />
                        </div>
                        <TextField
                          fullWidth
                          id="outlined-basic"
                          disabled={!data}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                Rp.
                              </InputAdornment>
                            ),
                          }}
                          value={g.price_grosirs}
                          onChange={(e) => {
                            let numericValue = e.target.value;
                            if (numericValue === "") {
                              numericValue = "0";
                            }
                            formik.setFieldValue(
                              "price.price_grosir",
                              formik.values.price.price_grosir.map(
                                (g1, idx) => {
                                  return index === idx
                                    ? {
                                        ...g,
                                        price_grosirs: parseInt(
                                          numericValue.replace(/[^0-9]/g, ""),
                                          10,
                                        ),
                                      }
                                    : g1;
                                },
                              ),
                            );
                          }}
                        />
                        <button
                          type="button"
                          className="ml-[10px]"
                          disabled={!data}
                          onClick={() => {
                            formik.setFieldValue("price.price_grosir", [
                              ...formik.values.price.price_grosir.slice(
                                0,
                                index,
                              ),
                              ...formik.values.price.price_grosir.slice(
                                index + 1,
                              ),
                            ]);
                          }}
                        >
                          <DeleteIcon sx={{ color: "black" }} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <p
                  className="text-blue-500 hover:underline hover:text-blue-500 cursor-pointer mt-4 text-right w-full"
                  onClick={() => {
                    formik.setFieldValue("price.price_grosir", [
                      ...formik.values.price.price_grosir,
                      { minimum: 0, maximum: 0, price_grosirs: 0 },
                    ]);
                  }}
                >
                  +Tambah harga grosir baru
                </p>

                <div className="flex justify-between items-center mt-4">
                  <p className="font-bold">Pengingat Produk Habis</p>
                  <Switch
                    checked={formik.values.status_reminder_stock}
                    disabled={!data}
                    onClick={() => {
                      if (formik.values.status_reminder_stock)
                        formik.setFieldValue("reminder_stock", 5);
                      formik.setFieldValue(
                        "status_reminder_stock",
                        !formik.values.status_reminder_stock,
                      );
                    }}
                  />
                </div>
                {formik.values.status_reminder_stock ? (
                  <div className="flex flex-row gap-2 items-center mt-4">
                    <p>Nyalakan Pengingat jika stok mencapai</p>
                    <div className="flex">
                      <button
                        className="bg-[#FF7A5D] p-3 mr-[10px] rounded-lg disabled:bg-gray-500"
                        disabled={!data}
                        type="button"
                        onClick={() => {
                          if (formik.values.reminder_stock === 0) return;
                          formik.setFieldValue(
                            "reminder_stock",
                            formik.values.reminder_stock - 1,
                          );
                        }}
                      >
                        -
                      </button>
                      <input
                        type="text"
                        className="border rounded-lg w-[69px] p-3"
                        disabled={!data}
                        value={formik.values.reminder_stock}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "reminder_stock",
                            Number(e.target.value),
                          )
                        }
                      />
                      <button
                        className="bg-[#5DFFBB] p-3 ml-[10px] rounded-lg disabled:bg-gray-500"
                        type="button"
                        disabled={!data}
                        onClick={() => {
                          formik.setFieldValue(
                            "reminder_stock",
                            formik.values.reminder_stock + 1,
                          );
                        }}
                      >
                        +
                      </button>
                    </div>
                    <FormControl sx={{ marginLeft: 3, width: "50%" }}>
                      <InputLabel id="demo-simple-select-label">
                        Pilihan Package
                      </InputLabel>
                      <Select
                        label="Pilihan Package"
                        value={formik.values.reminder_uom}
                        disabled={formik.values.reminder_stock === false}
                        onChange={(e) =>
                          formik.setFieldValue("reminder_uom", e.target.value)
                        }
                      >
                        <MenuItem value={0}>Primer</MenuItem>
                        <MenuItem value={1}>Sekunder</MenuItem>
                        <MenuItem value={2}>Tersier</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                ) : null}

                {isWarehouse ? null : (
                  <div>
                    <p
                      className="text-blue-500 text-end hover:underline hover:text-blue-500 cursor-pointer mt-4"
                      onClick={handleAddPlatform}
                    >
                      +Tambah Platform lain
                    </p>
                    {platforms &&
                    platforms !== undefined &&
                    formik.values.price.platform_price.length > 0 ? (
                      <>
                        {formik.values.price.platform_price.map(
                          (plat, index) => {
                            return (
                              <div
                                className="flex justify-center items-center mt-[20px]"
                                key={plat.platform_id}
                              >
                                <FormControl fullWidth sx={{ marginRight: 1 }}>
                                  <InputLabel>Kategori</InputLabel>
                                  <Select
                                    id="add-category"
                                    value={plat.platform_id}
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "price.platform_price",
                                        formik.values.price.platform_price.map(
                                          (p1) => {
                                            return p1.platform_id ===
                                              plat.platform_id
                                              ? {
                                                  ...plat,
                                                  platform_id: e.target.value,
                                                }
                                              : p1;
                                          },
                                        ),
                                      );
                                    }}
                                  >
                                    {platforms.map((p) => {
                                      return (
                                        <MenuItem
                                          key={p.id}
                                          value={p.id}
                                          disabled={
                                            formik.values.price.platform_price.find(
                                              (f) => f.platform_id === p.id,
                                            )
                                              ? true
                                              : false
                                          }
                                          sx={{
                                            display:
                                              formik.values.price.platform_price.find(
                                                (f) => f.platform_id === p.id,
                                              )
                                                ? "none"
                                                : null,
                                          }}
                                        >
                                          {p.name}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                                <TextField
                                  id="outlined-basic"
                                  label="Harga Jual"
                                  variant="outlined"
                                  fullWidth
                                  value={plat.price}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "price.platform_price",
                                      formik.values.price.platform_price.map(
                                        (p1) => {
                                          return p1.platform_id ===
                                            plat.platform_id
                                            ? {
                                                ...plat,
                                                price: Number(e.target.value),
                                              }
                                            : p1;
                                        },
                                      ),
                                    )
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        Rp.
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          edge="end"
                                          onClick={() => {
                                            if (
                                              formik.values.price.platform_price
                                                .length === 1
                                            ) {
                                              formik.setFieldValue(
                                                "price.platform_price",
                                                [],
                                              );
                                            } else {
                                              formik.setFieldValue(
                                                "price.platform_price",
                                                [
                                                  ...formik.values.price.platform_price.slice(
                                                    0,
                                                    index,
                                                  ),
                                                  ...formik.values.price.platform_price.slice(
                                                    index + 1,
                                                  ),
                                                ],
                                              );
                                            }
                                          }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>
                            );
                          },
                        )}
                      </>
                    ) : null}
                  </div>
                )}
                <div className="grid grid-cols-2 mt-10 gap-12">
                  <button
                    className="bg-red-700 text-white font-semibold p-3 rounded-lg"
                    onClick={() =>
                      navigate(
                        `${
                          isWarehouse ? "/central-warehouse" : ""
                        }/inventory/produk-hold/detail-product/${id}`,
                      )
                    }
                  >
                    Batal
                  </button>
                  <button
                    className="bg-orange-400 text-white font-semibold p-3 rounded-lg disabled:bg-gray-300"
                    disabled={
                      formik.values.category_id.length === 0 ||
                      (data.batch_details.filter(
                        (item) => item.from_purchase === true,
                      ).length > 0
                        ? formik.values.batch_details.reduce((acc, num) => {
                            return acc + num.quantity;
                          }, 0) === 0 ||
                          formik.values.batch_details.reduce((acc, num) => {
                            return acc + num.quantity;
                          }, 0) !== count ||
                          count === 0 ||
                          qtyType === ""
                        : false)
                    }
                    type="submit"
                  >
                    Konfirmasi
                  </button>
                </div>
              </div>
            </form>
          </>
        </>
      ) : (
        <p>Tidak Ditemukan!</p>
      )}

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default EditDetailProductHold;
